<template>
  <div class="flex flex-col gap-2">
    <ui-popover :open="isOpen" @update:open="(value) => isOpen = value">
      <ui-popover-trigger as-child>
        <button
          type="button"
          data-slot="multi-select-trigger"
          role="combobox"
          :aria-expanded="isOpen"
          :disabled="disabled || loading"
          class="glass-input flex h-9 w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-sm whitespace-nowrap focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span class="flex min-w-0 items-center gap-2 truncate text-muted-foreground">
            <loader-circle-icon v-if="loading" class="size-4 animate-spin" />
            {{ loading ? tr('Loading...') : placeholder }}
          </span>
          <chevrons-up-down-icon class="size-4 shrink-0 opacity-50" />
        </button>
      </ui-popover-trigger>
      <ui-popover-content align="start" class="w-(--reka-popover-trigger-width) min-w-56 p-0">
        <ui-command>
          <ui-command-input :placeholder="tr('Search') + '...'" />
          <ui-command-list class="max-h-72">
            <ui-command-empty>{{ tr('No results') }}</ui-command-empty>
            <ui-command-group>
              <ui-command-item
                v-for="(option, index) in filteredOptions"
                :key="'multi-select-option-' + index"
                :value="optionLabel(option)"
                :class="option[disabledKey] ? 'opacity-50' : ''"
                @select="() => onSelectOption(option)"
              >
                <span
                  v-tooltip.left="option[disabledKey] === true ? (option.disabled_message || disabledOptionTooltip || null) : null"
                  class="flex min-w-0 items-center gap-2"
                >
                  <i v-if="option.platform" :class="[ 'icon size-4 shrink-0', option.platform ]" />
                  <span
                    v-if="'status' in option"
                    :class="[ 'size-2 shrink-0 rounded-full', option.status ? 'bg-success' : 'bg-destructive' ]"
                  />
                  <span class="truncate">{{ optionLabel(option) }}</span>
                </span>
              </ui-command-item>
            </ui-command-group>
          </ui-command-list>
        </ui-command>
      </ui-popover-content>
    </ui-popover>

    <div v-if="options.length !== 0 && modelValue.length !== 0" class="flex flex-wrap gap-1.5">
      <span
        v-for="(item, key) in modelValue"
        :key="'value-object' + key"
        data-slot="multi-select-value"
        class="inline-flex max-w-full items-center gap-1.5 rounded-md py-1 pr-1 pl-2.5 text-xs font-medium text-white"
        :style="{ background: isDeleted(item) ? deletedColor : color }"
      >
        <span
          v-if="valueOption(item) !== null && 'status' in valueOption(item)"
          :class="[ 'size-2 shrink-0 rounded-full ring-1 ring-white', valueOption(item).status ? 'bg-success' : 'bg-destructive' ]"
        />
        <i v-if="valueOption(item)?.platform" :class="[ 'icon white size-3.5 shrink-0', valueOption(item).platform ]" />
        <span class="min-w-0 [overflow-wrap:anywhere]">
          {{ valueOption(item) ? optionLabel(valueOption(item)) : '' }}
          <small v-if="isDeleted(item) && (valueOption(item)?.deletedPlusLabel ?? deletedPlusLabel)" class="block opacity-90">
            ({{ valueOption(item)?.deletedPlusLabel ?? deletedPlusLabel }})
          </small>
        </span>
        <button
          v-if="!disabled"
          type="button"
          data-slot="multi-select-remove"
          class="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded hover:bg-white/20"
          :aria-label="tr('Remove')"
          @click="() => removeItem(item)"
        >
          <x-icon class="size-3.5" />
        </button>
        <span v-else class="w-1.5" />
      </span>
    </div>
  </div>
</template>

<script>
import { ChevronsUpDown, LoaderCircle, X } from '@lucide/vue';

import debounce from '@/utils/debounce.js';

/**
 * Multiple choice picker: a searchable dropdown adds options, the chosen ones
 * show as removable chips. Disabled options (`disabledKey`) can't be added.
 */
export default {
  name: 'MultiSelect',
  components: {
    ChevronsUpDownIcon: ChevronsUpDown,
    LoaderCircleIcon: LoaderCircle,
    XIcon: X,
  },
  props: {
    modelValue: {
      type: Array,
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Select targeting',
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    color: {
      type: String,
      default: 'var(--primary)',
    },
    deletedColor: {
      type: String,
      default: '#e6564d',
    },
    deletedPlusLabel: {
      type: String,
      default: '',
    },
    valueKey: {
      type: String,
      required: false,
      default: 'value',
    },
    labelKey: {
      type: String,
      required: false,
      default: 'label',
    },
    disabledKey: {
      type: String,
      required: false,
      default: 'disabled',
    },
    disabledClickMessage: {
      type: String,
      required: false,
      default: () => '',
    },
    loading: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    disabledOptionTooltip: {
      type: String,
      required: false,
      default: () => '',
    },
  },
  emits: [ 'update:modelValue' ],
  data() {
    return {
      debouncedInitSelectedValues: debounce(() => this.initSelectedValues(), 50),
      isOpen: false,
    };
  },
  computed: {
    filteredOptions() {
      return this.options.filter(item => !this.itemIsSelected(item[this.valueKey]));
    },
  },
  watch: {
    options: {
      immediate: true,
      handler() {
        this.debouncedInitSelectedValues();
      },
    },
    loading(val) {
      if (!val) {
        this.debouncedInitSelectedValues();
      }
    },
  },
  methods: {
    initSelectedValues() {
      if (this.modelValue.length !== 0 && !this.loading) {
        const optionValueList = this.options.map(i => i[this.valueKey]);
        let value = [ ...JSON.parse(JSON.stringify(this.modelValue)) ];
        value = value.filter(item => {
          if (typeof item === 'object' && 'id' in item) {
            return optionValueList.map(i => i.id).includes(item.id);
          }

          return optionValueList.includes(item);
        });

        if (JSON.stringify(value) !== JSON.stringify(this.modelValue)) {
          this.$emit('update:modelValue', value);
        }
      }
    },
    itemIsSelected(item) {
      return this.modelValue.some(obj => (typeof obj === 'object' && 'id' in obj && obj.id === item.id) || JSON.stringify(obj) === JSON.stringify(item));
    },
    removeItem(removableItem) {
      const newValue = this.modelValue.filter(item => item !== removableItem);
      this.$emit('update:modelValue', newValue);
    },
    valueOption(value) {
      return this.options.find(item => {
        if (typeof item[this.valueKey] === 'object' && 'id' in item[this.valueKey]) {
          return item[this.valueKey].id == value.id;
        }

        return JSON.stringify(item[this.valueKey]) == JSON.stringify(value);
      }) ?? null;
    },
    // Option labels are translation keys, like the old global vue-select config.
    optionLabel(option) {
      return this.tr(String(option[this.labelKey] ?? ''));
    },
    isDeleted(item) {
      return typeof item === 'object' && item !== null && 'deleted_at' in item && item.deleted_at !== null;
    },
    onSelectOption(selectedOption) {
      if (selectedOption[this.disabledKey]) {
        if (this.disabledClickMessage) {
          this.$toast.clear();
          this.$toast.error(this.disabledClickMessage);
        }

        return;
      }

      const value = selectedOption[this.valueKey];
      if (!this.itemIsSelected(value)) {
        this.$emit('update:modelValue', [ ...this.modelValue, value ]);
      }
      this.isOpen = false;
    },
  },
};
</script>
